import React, { useEffect, useState } from 'react';
import { styles } from './MyAccount.css';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import { trackLogout } from '../../../hooks/useLoginTracking/useLoginTracking';
import { handleLogout } from '../../../utils/signInOutUp/AuthenticationHelper';
import { trackHeaderClicks } from '../header/headerTrackingHelpers';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { useCouponContext } from '../../../contexts/CouponContextProvider';
import { useCustomerApi } from '../customers/hooks/customerApi';
import { UserDetails } from '../customers/components/manageAccount/userDetails/UserDetails';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { PerksContent } from '../perks/components/perksContent';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';

const distanceToBorders = (elementClass: string): { right: number; bottom: number } => {
  const referenceElem = document.querySelector(`.${elementClass}`);
  if (!referenceElem) return { right: 0, bottom: 0 };
  const elemBoundaries = referenceElem.getBoundingClientRect();
  const pxToWindowRight = window.innerWidth - elemBoundaries.right;
  const pxToWindowBottom = window.innerHeight - elemBoundaries.bottom;
  return { right: pxToWindowRight, bottom: pxToWindowBottom };
};

interface MyAccountProps {
  onHideSubmenu: () => void;
}

export const MyAccount = ({ onHideSubmenu }: MyAccountProps) => {
  const { addressesCount, isPerks, perksJoinDate } = useCustomerContext();
  const {
    isCouponSurfacingEnabled,
    coupons,
    newCouponCount,
    handleSelectCoupon,
    handleRemoveCoupon,
    handleViewCoupons,
  } = useCouponContext({ location: 'header' });
  const [elementClass, setElementClass] = useState('');
  const borderDistances = elementClass ? distanceToBorders(elementClass) : { right: 0, bottom: 0 };
  const shopifyCustomer = window.Shopify?.Customer || {};
  const isMobile = useBreakpoint('md', 'down');

  const { customer, setCustomer } = useCustomerApi();

  useEffect(() => {
    if (isCouponSurfacingEnabled && newCouponCount > 0) {
      handleViewCoupons();
    }
  }, []);
  useEffect(() => {
    setElementClass(styles.wrapper);
    if (isMobile) document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = '';
    };
  }, [isMobile]);

  const onLinkClick = (url: string, datalayerValue: string) => {
    trackHeaderClicks('myaccount-menu', datalayerValue);
    onHideSubmenu();
    window.location.href = `/account#/${url}`;
  };

  const onLogoutClick = () => {
    trackLogout({});
    handleLogout();
    onHideSubmenu();
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.container}
        style={{ right: `-${borderDistances.right}px`, height: `${borderDistances.bottom}px` }}
      >
        <div className={styles.accountHeader}>
          <UserDetails
            firstName={shopifyCustomer.firstName}
            lastName={shopifyCustomer.lastName}
            email={shopifyCustomer.email}
            userPhoto={shopifyCustomer.userPhoto}
            isPerks={isPerks}
            isHeader={true}
            startDate={perksJoinDate}
            avatarSize={53}
          />
        </div>
        <div className={styles.menuOptions}>
          <JBButton
            classes={styles.link}
            type="linkWithIcon"
            icon={<Icon name="InventoryBox" />}
            iconPosition="left"
            onClick={() => onLinkClick('', 'purchases')}
          >
            Purchases
          </JBButton>
          <JBButton
            classes={styles.link}
            type="linkWithIcon"
            icon={<Icon name="HeartEmpty" />}
            onClick={() => onLinkClick('wishlist', 'wishlist')}
          >
            Wishlist
          </JBButton>
          <JBButton
            classes={styles.link}
            type="linkWithIcon"
            icon={<Icon name="Notebook" />}
            onClick={() => onLinkClick('addresses', 'addresses')}
          >
            Addresses ({addressesCount})
          </JBButton>
          <JBButton
            classes={styles.link}
            type="linkWithIcon"
            icon={<Icon name="Cog" />}
            onClick={() => onLinkClick('manage-account', 'manageAccount')}
          >
            Manage account
          </JBButton>
        </div>
        {isCouponSurfacingEnabled ? (
          <PerksContent
            isPerks={isPerks}
            customerId={customer.id}
            coupons={coupons}
            onApplyCoupon={handleSelectCoupon}
            onRemoveCoupon={handleRemoveCoupon}
            onSetCustomer={setCustomer}
          />
        ) : null}
        <div className={styles.logoutContainer}>
          <hr className={styles.hr} />
          <JBButton classes={styles.link} type="link" onClick={onLogoutClick}>
            Log out
          </JBButton>
        </div>
      </div>
    </div>
  );
};
